import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /**
   * MoeMark: a page link (pagination-controls with on_click_handler
   * 'goToPage') renders that page of the section in place, keeps the
   * reader at the top of the results rather than the top of the document,
   * and REPLACES the history entry, so Back leaves the collection instead
   * of walking back through every page that was looked at. On the For
   * Business hub the grid sits under the families and the brand bands, so
   * a full reload put the reader back at the top of all of it - Moe,
   * 17 September 2026.
   *
   * @param {{ page?: number | string }} data - the link's URL parameters
   * @param {Event} event
   */
  async goToPage(data, event) {
    if (event) event.preventDefault();
    const page = Number(data && data.page) || 1;
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    // Where the results start now, minus the sticky header, measured before
    // the section is swapped so the number is not disturbed by the swap.
    const header = document.querySelector('#header-component, .header, header');
    const headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
    const top = Math.max(0, this.getBoundingClientRect().top + getScrollTop() - headerHeight - 8);

    try {
      // A copy: the renderer appends section_id to the URL it is given, and
      // that must not reach the address bar.
      await sectionRenderer.renderSection(this.sectionId, { url: new URL(url), cache: false });
    } catch (error) {
      window.location.href = url.toString();
      return;
    }
    history.replaceState(history.state, '', url.toString());
    scrollTo({ top, behavior: 'instant' });
  }

  /**
   * MoeMark: every page link inside the results goes through goToPage. A
   * plain listener, not the on:click attribute, because the component's
   * delegated events never reached a page link on the preview theme.
   *
   * @param {Event} event
   */
  #onPageLinkClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target && target.closest('a.pagination__link[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const page = new URL(link.href, window.location.href).searchParams.get('page') || '1';
    this.goToPage({ page }, event);
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onPageLinkClick);

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#onPageLinkClick);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
